// Unified Nutrition Service - Phase 1: Service Layer Restructuring
import { supabase } from '@/integrations/supabase/client';
import { BaseService, ServiceResponse, ServiceOptions } from '@/lib/services/BaseService';
import { CRUDService } from '@/lib/services/CRUDService';

// Unified interfaces for all nutrition types
export interface NutritionGoal {
  id: string;
  user_id: string;
  daily_calories: number;
  protein_grams: number;
  carbs_grams: number;
  fats_grams: number;
  goal_type: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface FoodItem {
  id: string;
  name: string;
  brand?: string;
  calories_per_100g: number;
  protein_per_100g: number;
  carbs_per_100g: number;
  fats_per_100g: number;
  fiber_per_100g?: number;
  category?: string;
  verified: boolean;
}

export interface Meal {
  id: string;
  user_id: string;
  meal_type: string;
  meal_date: string;
  total_calories: number;
  total_protein: number;
  total_carbs: number;
  total_fats: number;
  photo_url?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface FoodEntry {
  id: string;
  meal_id: string;
  food_id?: string;
  custom_food_name?: string;
  quantity_grams: number;
  calories: number;
  protein: number;
  carbs: number;
  fats: number;
  fiber?: number;
  ai_estimated: boolean;
  confidence_score?: number;
  created_at: string;
}

export interface NutritionDailySummary {
  id: string;
  user_id: string;
  date: string;
  total_calories: number;
  total_protein: number;
  total_carbs: number;
  total_fats: number;
  total_fiber: number;
  calories_burned: number;
  net_calories: number;
  goal_calories?: number;
  goal_protein?: number;
  goal_carbs?: number;
  goal_fats?: number;
  created_at: string;
  updated_at: string;
}

export interface FoodPhotoAnalysis {
  id: string;
  user_id: string;
  photo_url: string;
  identified_foods: any;
  estimated_portions: any;
  analysis_result: any;
  confidence_score: number;
  processing_status: string;
  error_message?: string;
  created_at: string;
  updated_at: string;
}

// Unified data request types
export type NutritionDataType = 
  | 'daily-summary'
  | 'meals-today' 
  | 'meals-for-date'
  | 'meals-for-range'
  | 'goals'
  | 'insights'
  | 'performance-correlations'
  | 'net-calories'
  | 'weekly-consistency';

export interface NutritionQueryParams {
  date?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
}

/**
 * Unified Nutrition Service - consolidates all nutrition-related operations
 * Replaces: NutritionService, NutritionDataService, NutritionAIService
 */
export class UnifiedNutritionService extends CRUDService {
  private static readonly COMPONENT_NAME = 'UnifiedNutritionService';

  // === GOALS MANAGEMENT ===
  static async getCurrentGoals(userId: string, options?: ServiceOptions): Promise<ServiceResponse<NutritionGoal | null>> {
    return this.handleRequest(async () => {
      const query = supabase
        .from('nutrition_goals')
        .select('*')
        .eq('user_id', userId)
        .eq('is_active', true)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      return await query;
    }, { ...options, componentName: this.COMPONENT_NAME });
  }

  static async setGoals(userId: string, goals: {
    daily_calories: number;
    protein_grams: number;
    carbs_grams: number;
    fats_grams: number;
    goal_type?: string;
  }, options?: ServiceOptions): Promise<ServiceResponse<NutritionGoal>> {
    return this.handleRequest(async () => {
      // Deactivate current goals first
      await supabase
        .from('nutrition_goals')
        .update({ is_active: false })
        .eq('user_id', userId)
        .eq('is_active', true);

      // Create new goal
      const query = supabase
        .from('nutrition_goals')
        .insert({
          user_id: userId,
          daily_calories: goals.daily_calories,
          protein_grams: goals.protein_grams,
          carbs_grams: goals.carbs_grams,
          fats_grams: goals.fats_grams,
          goal_type: goals.goal_type || 'maintain',
          is_active: true
        })
        .select()
        .single();

      return await query;
    }, { ...options, componentName: this.COMPONENT_NAME });
  }

  // === FOOD DATABASE ===
  static async searchFoods(query: string, limit = 20, options?: ServiceOptions): Promise<ServiceResponse<FoodItem[]>> {
    return this.handleRequest(async () => {
      const searchQuery = supabase
        .from('food_database')
        .select('*')
        .or(`name.ilike.%${query}%, brand.ilike.%${query}%`)
        .eq('verified', true)
        .order('name')
        .limit(limit);

      return await searchQuery;
    }, { ...options, componentName: this.COMPONENT_NAME });
  }

  static async getFoodById(foodId: string, options?: ServiceOptions): Promise<ServiceResponse<FoodItem | null>> {
    return this.handleRequest(async () => {
      const query = supabase
        .from('food_database')
        .select('*')
        .eq('id', foodId)
        .maybeSingle();

      return await query;
    }, { ...options, componentName: this.COMPONENT_NAME });
  }

  // === MEALS MANAGEMENT ===
  static async createMeal(userId: string, mealData: Partial<Meal>, options?: ServiceOptions): Promise<ServiceResponse<Meal>> {
    return this.createEntry('meals', {
      user_id: userId,
      ...mealData
    }, { ...options, componentName: this.COMPONENT_NAME });
  }

  static async getMeals(userId: string, startDate?: string, endDate?: string, options?: ServiceOptions): Promise<ServiceResponse<Meal[]>> {
    return this.handleRequest(async () => {
      let query = supabase
        .from('meals')
        .select('*')
        .eq('user_id', userId)
        .order('meal_date', { ascending: false })
        .order('created_at', { ascending: false });

      if (startDate) {
        query = query.gte('meal_date', startDate);
      }
      if (endDate) {
        query = query.lte('meal_date', endDate);
      }

      return await query;
    }, { ...options, componentName: this.COMPONENT_NAME });
  }

  static async updateMeal(mealId: string, mealData: Partial<Meal>, options?: ServiceOptions): Promise<ServiceResponse<Meal>> {
    return this.updateEntry('meals', mealId, mealData, { ...options, componentName: this.COMPONENT_NAME });
  }

  static async deleteMeal(mealId: string, options?: ServiceOptions): Promise<ServiceResponse<void>> {
    return this.handleRequest(async () => {
      // First delete all food entries for this meal
      const { error: foodEntriesError } = await supabase
        .from('food_entries')
        .delete()
        .eq('meal_id', mealId);

      if (foodEntriesError) throw foodEntriesError;

      // Then delete the meal itself
      const { error: mealError } = await supabase
        .from('meals')
        .delete()
        .eq('id', mealId);

      if (mealError) throw mealError;
      return { data: undefined, error: null };
    }, { ...options, componentName: this.COMPONENT_NAME });
  }

  // === FOOD ENTRIES ===
  static async addFoodToMeal(mealId: string, foodEntry: Partial<FoodEntry>, options?: ServiceOptions): Promise<ServiceResponse<FoodEntry>> {
    return this.createEntry('food_entries', {
      meal_id: mealId,
      ...foodEntry
    }, { ...options, componentName: this.COMPONENT_NAME });
  }

  static async getFoodEntries(mealId: string, options?: ServiceOptions): Promise<ServiceResponse<FoodEntry[]>> {
    return this.handleRequest(async () => {
      const query = supabase
        .from('food_entries')
        .select(`
          *,
          food_database (
            name,
            brand,
            category
          )
        `)
        .eq('meal_id', mealId)
        .order('created_at');

      return await query;
    }, { ...options, componentName: this.COMPONENT_NAME });
  }

  static async updateFoodEntry(entryId: string, updates: Partial<FoodEntry>, options?: ServiceOptions): Promise<ServiceResponse<FoodEntry>> {
    return this.updateEntry('food_entries', entryId, updates, { ...options, componentName: this.COMPONENT_NAME });
  }

  static async deleteFoodEntry(entryId: string, options?: ServiceOptions): Promise<ServiceResponse<null>> {
    return this.deleteEntry('food_entries', entryId, { ...options, componentName: this.COMPONENT_NAME });
  }

  // === DAILY SUMMARIES ===
  static async getDailySummary(userId: string, date: string, options?: ServiceOptions): Promise<ServiceResponse<NutritionDailySummary | null>> {
    return this.handleRequest(async () => {
      const query = supabase
        .from('nutrition_daily_summary')
        .select('*')
        .eq('user_id', userId)
        .eq('date', date)
        .maybeSingle();

      return await query;
    }, { ...options, componentName: this.COMPONENT_NAME });
  }

  static async getWeeklySummaries(userId: string, weeks = 4, options?: ServiceOptions): Promise<ServiceResponse<NutritionDailySummary[]>> {
    return this.handleRequest(async () => {
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - (weeks * 7));

      const query = supabase
        .from('nutrition_daily_summary')
        .select('*')
        .eq('user_id', userId)
        .gte('date', startDate.toISOString().split('T')[0])
        .order('date', { ascending: false });

      return await query;
    }, { ...options, componentName: this.COMPONENT_NAME });
  }

  // === AI PHOTO ANALYSIS ===
  static async analyzePhoto(userId: string, photoUrl: string, options?: ServiceOptions): Promise<ServiceResponse<FoodPhotoAnalysis>> {
    return this.handleRequest(async () => {
      // Create initial record
      const { data: analysisRecord, error: createError } = await supabase
        .from('food_photo_analysis')
        .insert({
          user_id: userId,
          photo_url: photoUrl,
          processing_status: 'pending'
        })
        .select()
        .single();

      if (createError) throw createError;

      // Call AI analysis edge function
      const { data: analysisResult, error: analysisError } = await supabase.functions.invoke(
        'ai-nutrition-analysis',
        {
          body: {
            analysisId: analysisRecord.id,
            photoUrl: photoUrl,
            userId: userId
          }
        }
      );

      if (analysisError) {
        // Update record with error
        await supabase
          .from('food_photo_analysis')
          .update({
            processing_status: 'failed',
            error_message: analysisError.message
          })
          .eq('id', analysisRecord.id);
        
        throw analysisError;
      }

      return { data: analysisResult?.data || analysisRecord, error: null };
    }, { ...options, componentName: this.COMPONENT_NAME });
  }

  static async getPhotoAnalysis(analysisId: string, options?: ServiceOptions): Promise<ServiceResponse<FoodPhotoAnalysis | null>> {
    return this.handleRequest(async () => {
      const query = supabase
        .from('food_photo_analysis')
        .select('*')
        .eq('id', analysisId)
        .maybeSingle();

      return await query;
    }, { ...options, componentName: this.COMPONENT_NAME });
  }

  // === NUTRITION INSIGHTS ===
  static async getNutritionInsights(userId: string, days = 30, options?: ServiceOptions): Promise<ServiceResponse<any>> {
    return this.handleSupabaseFunction(
      'nutrition-insights',
      {
        userId,
        days
      },
      { ...options, componentName: this.COMPONENT_NAME }
    );
  }

  // === NET CALORIES ===
  static async calculateNetCalories(userId: string, date: string, options?: ServiceOptions): Promise<ServiceResponse<{
    consumed: number;
    burned: number;
    net: number;
  }>> {
    return this.handleRequest(async () => {
      // Get daily nutrition summary
      const { data: nutritionData } = await supabase
        .from('nutrition_daily_summary')
        .select('total_calories, calories_burned')
        .eq('user_id', userId)
        .eq('date', date)
        .maybeSingle();

      const consumed = nutritionData?.total_calories || 0;
      const burned = nutritionData?.calories_burned || 0;

      return {
        data: {
          consumed,
          burned,
          net: consumed - burned
        },
        error: null
      };
    }, { ...options, componentName: this.COMPONENT_NAME });
  }

  // === UNIFIED DATA FETCHING ===
  static async getNutritionData<T extends NutritionDataType>(
    type: T,
    userId: string,
    params?: NutritionQueryParams,
    options?: ServiceOptions
  ): Promise<ServiceResponse<any>> {
    switch (type) {
      case 'daily-summary':
        const today = new Date().toISOString().split('T')[0];
        return this.getDailySummary(userId, today, options);
      
      case 'meals-today':
        const todayStr = new Date().toISOString().split('T')[0];
        return this.getMeals(userId, todayStr, todayStr, options);
      
      case 'meals-for-date':
        if (!params?.date) throw new Error('Date parameter required for meals-for-date');
        return this.getMeals(userId, params.date, params.date, options);
      
      case 'meals-for-range':
        if (!params?.startDate || !params?.endDate) {
          throw new Error('Date range parameters required for meals-for-range');
        }
        return this.getMeals(userId, params.startDate, params.endDate, options);
      
      case 'goals':
        return this.getCurrentGoals(userId, options);
      
      case 'weekly-consistency':
        return this.getWeeklySummaries(userId, 1, options);
      
      default:
        throw new Error(`Unknown nutrition data type: ${type}`);
    }
  }

  // === CACHE INVALIDATION ===
  static invalidateUserData(userId: string, queryClient: any) {
    const queryKeys = [
      'daily-summary',
      'meals-today', 
      'goals',
      'insights',
      'performance-correlations',
      'net-calories',
      'weekly-consistency'
    ];

    queryKeys.forEach(key => {
      queryClient.invalidateQueries({ 
        queryKey: [key, userId],
        exact: false 
      });
    });
  }

  // === BATCH OPERATIONS ===
  static async batchGetNutritionData(userId: string, types: NutritionDataType[], options?: ServiceOptions) {
    return this.handleRequest(async () => {
      const promises = types.map(type => this.getNutritionData(type, userId, {}, options));
      const results = await Promise.allSettled(promises);
      
      const data: Record<string, any> = {};
      const errors: string[] = [];
      
      results.forEach((result, index) => {
        const type = types[index];
        if (result.status === 'fulfilled') {
          data[type] = result.value.data;
        } else {
          errors.push(`${type}: ${result.reason.message}`);
        }
      });
      
      return {
        data: { data, errors: errors.length > 0 ? errors : null },
        error: null
      };
    }, { ...options, componentName: this.COMPONENT_NAME });
  }
}